import { GeneralService } from "./GeneralService";
import { ApiKeyOrGetter } from "./GeneralService";

// TODO: This interface should be generated from the backend AgentAction model
interface AgentAction {
  id?: number;
  date_created?: number;
  agent_id: number;
  action_name: string;
  action_type: string;
  description: string;
  status?: string;
  is_default?: boolean;
  action_result_type?: string;
  action_config?: {
    [k: string]: any;
  };
  meta?: {
    [k: string]: any;
  };
  invocation_mode?: "foreground" | "background";
  content_hash?: string;
}

interface AgentActionVersion {
  id?: number;
  date_created?: number;
  agent_action_id: number;
  version_number: string;
  major: number;
  minor: number;
  patch: number;
  content_hash: string;
  action_name: string;
  action_type: string;
  description: string;
  action_config?: {
    [k: string]: any;
  };
  action_result_type: string;
  invocation_mode: "foreground" | "background";
  version_notes?: string;
  created_by_user_id?: number;
  is_latest: boolean;
  meta?: {
    [k: string]: any;
  };
}

interface AgentActionVersionStatus {
  action: AgentAction;
  latest_version?: string;
  has_unpublished_changes: boolean;
  version_count: number;
}

export class AgentActionService {
  
  /**
   * Get all actions for an agent
   */
  static getAgentActions(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/actions`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get action details by ID
   */
  static getAction(
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/actions/${actionId}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Create a new action for an agent
   */
  static createAgentAction(
    agentId: number,
    action: Partial<AgentAction>,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/agents/${agentId}/actions`,
      JSON.stringify(action),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Update an action
   */
  static updateAction(
    actionId: number,
    updates: Partial<AgentAction>,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/actions/${actionId}`,
      JSON.stringify(updates),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Delete an action
   */
  static deleteAction(
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/agent/actions/${actionId}`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Set an action as the default for an agent
   */
  static setDefaultAction(
    agentId: number,
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/agents/${agentId}/actions/${actionId}/default`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get action with version status
   */
  static getActionVersionStatus(
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/actions/${actionId}/version-status`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get version history for an action
   */
  static getActionVersions(
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter,
    limit?: number
  ) {
    const queryParams = limit ? `?limit=${limit}` : "";
    return GeneralService.get(
      `/agent/actions/${actionId}/versions${queryParams}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get a specific version
   */
  static getActionVersion(
    actionId: number,
    versionNumber: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/actions/${actionId}/versions/${encodeURIComponent(versionNumber)}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Publish current draft as new version
   */
  static publishActionVersion(
    actionId: number,
    increment: "major" | "minor" | "patch",
    versionNotes: string | undefined,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/actions/${actionId}/publish`,
      JSON.stringify({ increment, version_notes: versionNotes }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Overwrite draft from a specific version
   */
  static draftFromActionVersion(
    actionId: number,
    versionNumber: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/actions/${actionId}/draft-from-version/${encodeURIComponent(versionNumber)}`,
      JSON.stringify({}),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
} 